import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import NationalitySelect from '../components/NationalitySelect';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  User, 
  FileText, 
  Settings, 
  LogOut, 
  Edit,
  Save,
  X,
  Calendar,
  Loader2,
  Award,
  GraduationCap,
  Camera,
  Upload,
  Download,
  Plus,
  History,
  ChevronDown,
  ChevronUp,
  PlayCircle,
  Video,
  CreditCard,
  Send,
  Banknote,
  Building2,
  Receipt,
  AlertTriangle,
  Info,
  Check
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { DocumentCamera, compressImage } from '../components/DocumentCamera';
import { FaceCamera } from '../components/FaceCamera';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MyPage() {
  const { language, t } = useLanguage();
  const fileInputRef = useRef(null);
  const idFrontRef = useRef(null);
  const idBackRef = useRef(null);
  const certFrontRef = useRef(null);
  const certBackRef = useRef(null);
  const idFrontCameraRef = useRef(null);
  const idBackCameraRef = useRef(null);
  const certFrontCameraRef = useRef(null);
  const certBackCameraRef = useRef(null);
  const { user, logout, updateProfile, isAuthenticated, loading: authLoading, getAuthHeaders, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewCertDialog, setViewCertDialog] = useState(false);
  const [viewCertData, setViewCertData] = useState(null);
  const [certImageUrl, setCertImageUrl] = useState(null);
  const [certImageLoading, setCertImageLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [idDocTypeMypage, setIdDocTypeMypage] = useState('residence_card');
  const [mypageCameraTarget, setMypageCameraTarget] = useState(null);
  const [qualificationsDialog, setQualificationsDialog] = useState(false);
  const [profileIncompleteDialog, setProfileIncompleteDialog] = useState(false);
  const [missingFields, setMissingFields] = useState([]);
  const tabsContentRef = useRef(null);
  const [userInvoices, setUserInvoices] = useState([]);
  const [userGuides, setUserGuides] = useState([]);
  const [bankInfo, setBankInfo] = useState({});
  const [invMethod, setInvMethod] = useState({});
  const [invReceipt, setInvReceipt] = useState({});
  const [payLoading, setPayLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('applications');
  const [showIdFrontHistory, setShowIdFrontHistory] = useState(false);
  const [showIdBackHistory, setShowIdBackHistory] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showCertFrontHistory, setShowCertFrontHistory] = useState(false);
  const [showCertBackHistory, setShowCertBackHistory] = useState(false);
  const [imageModal, setImageModal] = useState({ isOpen: false, src: '', alt: '' });
  const [courseProgress, setCourseProgress] = useState({});

  // Certificate request states
  const [showCertDialog, setShowCertDialog] = useState(false);
  const [certRequestCourse, setCertRequestCourse] = useState(null);
  const [certRequestType, setCertRequestType] = useState(null);
  const [submittingCertRequest, setSubmittingCertRequest] = useState(false);
  const [myCertRequests, setMyCertRequests] = useState([]);
  const [practicalReservations, setPracticalReservations] = useState([]);
  const [showPracticalDialog, setShowPracticalDialog] = useState(false);
  const [practicalForm, setPracticalForm] = useState({ courseId: '', preferredDate1: '', preferredDate2: '', preferredDate3: '', message: '' });

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/register');
    }
  }, [authLoading, isAuthenticated, navigate]);

  // Prompt users without a profile photo to take one (after login / on My Page).
  const [showFacePrompt, setShowFacePrompt] = useState(false);
  const [showFaceCamera, setShowFaceCamera] = useState(false);
  useEffect(() => {
    if (!authLoading && isAuthenticated && user && !user.profileImage) {
      if (sessionStorage.getItem('facePromptDismissed') !== '1') {
        setShowFacePrompt(true);
      }
    }
  }, [authLoading, isAuthenticated, user]);

  const handleFaceCaptured = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API}/users/me/profile-image`, formData, {
        headers: { ...getAuthHeaders() },
      });
      if (res.data?.success) {
        setShowFaceCamera(false);
        setShowFacePrompt(false);
        toast.success(t('mypage.profile.imageUpdated'));
        if (refreshUser) { await refreshUser(); } else { window.location.reload(); }
        return true;
      }
      return false;
    } catch (err) {
      console.error('Profile photo upload error:', err?.response?.data || err);
      toast.error(t('mypage.profile.imageUploadError'));
      // Keep the camera open so the user can retry without reopening it.
      return false;
    }
  };

  const dismissFacePrompt = () => {
    sessionStorage.setItem('facePromptDismissed', '1');
    setShowFacePrompt(false);
  };

  // Fetch user's applications
  useEffect(() => {
    const fetchApplications = async () => {
      if (!isAuthenticated) return;
      
      try {
        const response = await axios.get(`${API}/users/me/applications`, {
          headers: getAuthHeaders()
        });
        const apps = response.data || [];
        setApplications(apps);
        
        // Fetch certificate requests
        try {
          const certReqRes = await axios.get(`${API}/users/me/certificate-requests`, {
            headers: getAuthHeaders()
          });
          setMyCertRequests(certReqRes.data || []);
        } catch (err) {
          console.error('Error fetching certificate requests:', err);
        }

        // Fetch practical reservations
        try {
          const practicalRes = await axios.get(`${API}/users/practical-reservations`, {
            headers: getAuthHeaders()
          });
          setPracticalReservations(practicalRes.data || []);
        } catch (err) {
          console.error('Error fetching practical reservations:', err);
        }

        // Fetch invoices
        let invoicesData = [];
        try {
          const invoiceRes = await axios.get(`${API}/users/me/invoices`, {
            headers: getAuthHeaders()
          });
          invoicesData = invoiceRes.data?.invoices || [];
          setUserInvoices(invoicesData);
          setBankInfo(invoiceRes.data?.bankInfo || {});
        } catch (err) {
          console.error('Error fetching invoices:', err);
        }

        try {
          const guidesRes = await axios.get(`${API}/users/me/training-guides`, {
            headers: getAuthHeaders()
          });
          setUserGuides(guidesRes.data || []);
        } catch (err) {
          console.error('Error fetching guides:', err);
        }
        
        // Fetch progress for confirmed + paid on-demand/hybrid courses
        const progressPromises = apps
          .filter(app => {
            if (app.status !== 'confirmed') return false;
            if (app.courseType !== 'on-demand' && app.courseType !== 'hybrid') return false;
            const inv = invoicesData.find(i => i.applicationId === app.id);
            return inv && inv.status === 'paid';
          })
          .map(async (app) => {
            try {
              const progressRes = await axios.get(`${API}/courses/${app.courseId}/my-progress`, {
                headers: getAuthHeaders()
              });
              return { courseId: app.courseId, progress: progressRes.data };
            } catch (err) {
              console.error(`Error fetching progress for ${app.courseId}:`, err);
              return { courseId: app.courseId, progress: null };
            }
          });
        
        const progressResults = await Promise.all(progressPromises);
        const progressMap = {};
        progressResults.forEach(({ courseId, progress }) => {
          if (progress) {
            progressMap[courseId] = progress;
          }
        });
        setCourseProgress(progressMap);
      } catch (error) {
        console.error('Error fetching applications:', error);
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated) {
      fetchApplications();
    }
  }, [isAuthenticated, getAuthHeaders]);

  // Refresh only the invoices list (used after payment / method selection)
  const refreshInvoices = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/users/me/invoices`, { headers: getAuthHeaders() });
      setUserInvoices(res.data?.invoices || []);
      setBankInfo(res.data?.bankInfo || {});
    } catch (err) {
      console.error('Error refreshing invoices:', err);
    }
  }, [getAuthHeaders]);

  // Save the chosen offline payment method (bank transfer / cash) + receipt preference
  const handleSelectOfflineMethod = async (inv, method) => {
    setPayLoading(true);
    try {
      await axios.post(`${API}/users/me/invoices/${inv.id}/payment-method`,
        { paymentMethod: method, receiptRequested: !!invReceipt[inv.id] },
        { headers: getAuthHeaders() });
      toast.success(t('mp.pay.methodSaved'));
      await refreshInvoices();
    } catch (err) {
      toast.error(t('mp.pay.methodFailed'));
    } finally {
      setPayLoading(false);
    }
  };

  // Start a Stripe checkout for the invoice
  const handleStripePay = async (inv) => {
    setPayLoading(true);
    try {
      const res = await axios.post(`${API}/payments/checkout`,
        { invoiceId: inv.id, originUrl: window.location.origin },
        { headers: getAuthHeaders() });
      if (res.data?.checkoutUrl) {
        window.location.href = res.data.checkoutUrl;
      } else {
        toast.error(t('mp.pay.checkoutFailed'));
        setPayLoading(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t('mp.pay.checkoutFailed'));
      setPayLoading(false);
    }
  };

  // Handle return from Stripe checkout (?payment=success&session_id=...)
  useEffect(() => {
    if (!isAuthenticated) return;
    const params = new URLSearchParams(window.location.search);
    const payment = params.get('payment');
    const sessionId = params.get('session_id');
    if (payment === 'success' && sessionId) {
      setActiveTab('invoices');
      let attempts = 0;
      const poll = async () => {
        attempts += 1;
        try {
          const res = await axios.get(`${API}/payments/status/${sessionId}`);
          if (res.data?.paymentStatus === 'paid') {
            toast.success(t('mp.pay.paidSuccess'));
            await refreshInvoices();
            return;
          }
        } catch (err) { /* ignore transient */ }
        if (attempts < 6) setTimeout(poll, 2000);
        else toast.info(t('mp.pay.paidPending'));
      };
      poll();
      window.history.replaceState({}, '', '/mypage');
    } else if (payment === 'cancel') {
      toast.info(t('mp.pay.cancelled'));
      window.history.replaceState({}, '', '/mypage');
    }
  }, [isAuthenticated, refreshInvoices]);

  // Initialize edit data when user changes
  useEffect(() => {
    if (user) {
      setEditData({
        name: user.name || '',
        nameKana: user.nameKana || '',
        phone: user.phone || '',
        birthDate: user.birthDate || '',
        gender: user.gender || '',
        postalCode: user.postalCode || '',
        address: user.address || '',
        nationality: user.nationality || '',
        language: user.language || '',
        company: user.company || '',
        companyPostalCode: user.companyPostalCode || '',
        companyAddress: user.companyAddress || '',
        companyPhone: user.companyPhone || '',
        department: user.department || '',
        position: user.position || ''
      });
    }
  }, [user]);

  // Check for incomplete profile on login
  useEffect(() => {
    if (user && isAuthenticated && !authLoading) {
      const requiredFields = [
        { key: 'name', label: t('mypage.profile.name') },
        { key: 'phone', label: t('mypage.profile.phone') },
        { key: 'birthDate', label: t('mp.birthDate') },
        { key: 'address', label: t('mypage.profile.address') },
        { key: 'gender', label: t('mypage.profile.gender') },
        { key: 'nationality', label: t('mypage.profile.nationality') },
        { key: 'language', label: t('mypage.profile.language') },
      ];
      const missing = requiredFields.filter(f => !user[f.key] || user[f.key].trim() === '');
      if (missing.length > 0) {
        setMissingFields(missing);
        setProfileIncompleteDialog(true);
      }
    }
  }, [user, isAuthenticated, authLoading]);

  // Helper function to check if a course is fully completed
  const isCourseFullyCompleted = (app) => {
    const progress = courseProgress[app.courseId];
    if (!progress || !progress.videos || progress.videos.length === 0) {
      return false;
    }
    
    const totalVideos = progress.videos.length;
    const completedVideos = progress.videos.filter(v => v.completed).length;
    const progressPercent = Math.round((completedVideos / totalVideos) * 100);
    
    // Check if all videos completed and quizzes passed
    const allQuizzesPassed = progress.videos.every(v => 
      v.completed && (v.quizPassed === null || v.quizPassed === true)
    );
    
    return progressPercent === 100 && allQuizzesPassed;
  };

  // 受講中に表示する条件: 確定(confirmed) + 入金済み + オンデマンドまたはハイブリッド型
  const isActiveCourse = (app) => {
    if (app.status !== 'confirmed') return false;
    if (app.courseType !== 'on-demand' && app.courseType !== 'hybrid') return false;
    const invoice = userInvoices.find(inv => inv.applicationId === app.id);
    return invoice && invoice.status === 'paid';
  };

  // Certificate request functions
  const openCertificateDialog = (app, type) => {
    setCertRequestCourse(app);
    setCertRequestType(type);
    setShowCertDialog(true);
  };

  const submitCertificateRequest = async () => {
    if (!certRequestCourse || !certRequestType) return;
    
    setSubmittingCertRequest(true);
    try {
      const response = await axios.post(`${API}/courses/${certRequestCourse.courseId}/certificate-request`, {
        type: certRequestType,
        courseName: certRequestCourse.courseName || certRequestCourse.courseId,
        userName: user?.name
      }, {
        headers: getAuthHeaders()
      });
      
      // Update local state with new request
      setMyCertRequests(prev => [...prev, {
        id: response.data.requestId,
        courseId: certRequestCourse.courseId,
        type: certRequestType,
        status: 'pending',
        createdAt: new Date().toISOString()
      }]);
      
      toast.success(
        certRequestType === 'card' 
          ? (t('mypage.cardCertRequested'))
          : (t('mypage.digitalCertRequested'))
      );
      setShowCertDialog(false);
      setCertRequestCourse(null);
      setCertRequestType(null);
    } catch (error) {
      console.error('Certificate request error:', error);
      const errorDetail = error.response?.data?.detail;
      let errorMessage = t('mypage.certificates.requestFailed');
      
      if (errorDetail === 'Not all videos completed') {
        errorMessage = t('mypage.completeVideos');
      } else if (errorDetail === 'Not all quizzes passed') {
        errorMessage = t('mypage.passQuizzes');
      } else if (errorDetail === 'Practical training not passed') {
        errorMessage = t('mypage.certificates.needPracticalReserve');
      }
      
      toast.error(errorMessage);
    } finally {
      setSubmittingCertRequest(false);
    }
  };

  // Check if certificate request exists for a course and type
  const getCertRequestStatus = (courseId, type) => {
    const request = myCertRequests.find(r => r.courseId === courseId && r.type === type);
    return request ? request.status : null;
  };

  // Check practical training status for hybrid courses
  const getPracticalStatus = (courseId) => {
    const res = practicalReservations.find(r => r.courseId === courseId);
    return res || null;
  };

  const isPracticalPassed = (courseId) => {
    const res = getPracticalStatus(courseId);
    return res && res.result === 'pass';
  };

  const submitPracticalReservation = async () => {
    try {
      const res = await axios.post(`${API}/users/practical-reservations`, practicalForm, { headers: getAuthHeaders() });
      setPracticalReservations(prev => [...prev, res.data]);
      toast.success(t('mypage.practical.submitted'));
      setShowPracticalDialog(false);
      setPracticalForm({ courseId: '', preferredDate1: '', preferredDate2: '', preferredDate3: '', message: '' });
    } catch (e) {
      toast.error(e.response?.data?.detail || (t('mypage.practical.submitFailed')));
    }
  };

  // Check if any certificate has been issued for this course
  const hasCertificateIssued = (courseId) => {
    return myCertRequests.some(r => r.courseId === courseId && r.status === 'completed');
  };

  // Get issued certificate info for a course
  const getIssuedCertificate = (courseId) => {
    return myCertRequests.find(r => r.courseId === courseId && r.status === 'completed');
  };

  const handleLogout = () => {
    logout();
    toast.success(t('mypage.loggedOut'));
    navigate('/');
  };

  const handleSaveProfile = async () => {
    try {
      await updateProfile(editData);
      toast.success(t('mypage.profile.saveSuccess'));
      setEditMode(false);
    } catch (error) {
      toast.error(t('mypage.profile.saveError'));
    }
  };

  const handleDownloadCertificate = async (applicationId, courseName, applicantName, type = 'application') => {
    try {
      toast.loading(t('mypage.certificates.generating'));
      
      // Use different endpoint based on type
      const endpoint = type === 'cert-request' 
        ? `${API}/users/me/certificate-requests/${applicationId}/pdf`
        : `${API}/users/me/certificates/${applicationId}/pdf`;
      
      const response = await axios.get(endpoint, {
        headers: getAuthHeaders(),
        responseType: 'blob'
      });
      
      // Create download link with course name and user name
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const userName = applicantName || user?.name || '';
      link.setAttribute('download', `修了証_${courseName || applicationId}_${userName}様.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.dismiss();
      toast.success(t('mypage.certificates.downloaded'));
    } catch (error) {
      toast.dismiss();
      console.error('Certificate download error:', error);
      const errorMessage = error.response?.data?.detail || (t('mypage.certificates.downloadError'));
      toast.error(errorMessage);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('mypage.profile.imageSizeError'));
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/users/me/profile-image`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.success) {
        // Refresh user data
        window.location.reload();
        toast.success(t('mypage.profile.imageUpdated'));
      }
    } catch (error) {
      console.error('Image upload error:', error);
      toast.error(t('mypage.profile.imageUploadError'));
    }
  };

  // Resize image to 150 DPI (approximately 1200px max dimension for typical document)
  const resizeImageTo150DPI = (file) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      img.onload = () => {
        // 150 DPI for A4 document is approximately 1240x1754 pixels
        // We'll use max dimension of 1200px to keep reasonable quality
        const MAX_DIMENSION = 1200;
        let { width, height } = img;
        
        // Calculate new dimensions maintaining aspect ratio
        if (width > height) {
          if (width > MAX_DIMENSION) {
            height = Math.round((height * MAX_DIMENSION) / width);
            width = MAX_DIMENSION;
          }
        } else {
          if (height > MAX_DIMENSION) {
            width = Math.round((width * MAX_DIMENSION) / height);
            height = MAX_DIMENSION;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // Draw and compress
        ctx.drawImage(img, 0, 0, width, height);
        
        canvas.toBlob(
          (blob) => {
            if (blob) {
              // Create new file with same name
              const resizedFile = new File([blob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now(),
              });
              resolve(resizedFile);
            } else {
              reject(new Error('Failed to resize image'));
            }
          },
          'image/jpeg',
          0.85 // Quality 85%
        );
      };
      
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = URL.createObjectURL(file);
    });
  };

  const handleDocumentUpload = async (docType, file) => {
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error(t('mypage.documents.onlyImages'));
      return;
    }

    setUploadingDoc(docType);

    try {
      const compressed = await compressImage(file, idDocTypeMypage || 'residence_card');
      
      const formData = new FormData();
      formData.append('file', compressed);
      formData.append('docType', docType);

      const response = await axios.post(`${API}/users/me/documents`, formData, {
        headers: { ...getAuthHeaders() }
      });

      if (response.data.success) {
        toast.success(t('mypage.documents.uploadSuccess'));
        await refreshUser();
        return true;
      }
      return false;
    } catch (error) {
      console.error('Document upload error:', error?.response?.data || error);
      toast.error(t('mypage.documents.uploadError'));
      return false;
    } finally {
      setUploadingDoc(null);
    }
  };

  const handleMypageCameraCapture = async (file) => {
    const target = mypageCameraTarget;
    if (!target) return false;
    const ok = await handleDocumentUpload(target, file);
    if (ok) setMypageCameraTarget(null);
    return ok;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-800">{t('mypage.applications.pending')}</Badge>;
      case 'contacted':
        return <Badge className="bg-blue-100 text-blue-800">{t('mypage.applications.contacted')}</Badge>;
      case 'confirmed':
        return <Badge className="bg-purple-100 text-purple-800">{t('mypage.applications.confirmed')}</Badge>;
      case 'completed_passed':
        return <Badge className="bg-green-100 text-green-800">{t('mypage.applications.passed')}</Badge>;
      case 'completed_failed':
        return <Badge className="bg-orange-100 text-orange-800">{t('mypage.applications.failed')}</Badge>;
      case 'cancelled':
        return <Badge className="bg-red-100 text-red-800">{t('mypage.cancelled')}</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-800">{status}</Badge>;
    }
  };

  // 受講済み合格講習のカウント
  const completedCount = applications.filter(app => app.status === 'completed_passed').length;
  const unpaidCount = userInvoices.filter(i => i.status === 'unpaid').length;

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#00A651]" />
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8" data-testid="mypage">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <div className="relative group">
                {user.profileImage ? (
                  <img 
                    src={`${BACKEND_URL}${user.profileImage}`}
                    alt={user.name}
                    className="h-12 w-12 sm:h-16 sm:w-16 rounded-full object-cover border-2 border-[#00A651]"
                  />
                ) : (
                  <div className="bg-[#00A651] p-2 sm:p-3 rounded-full flex-shrink-0">
                    <User className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                  </div>
                )}
                <button
                  onClick={() => setShowFaceCamera(true)}
                  className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                >
                  <Camera className="h-5 w-5 text-white" />
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
              </div>
              <div className="min-w-0">
                <h1 className="text-base sm:text-xl font-bold text-gray-900 truncate">{user.name}</h1>
                <p className="text-gray-500 text-xs sm:text-sm truncate">{user.email}</p>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={handleLogout}
              className="text-red-600 border-red-300 hover:bg-red-50 flex-shrink-0 text-xs sm:text-sm px-2 sm:px-4"
            >
              <LogOut className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">{t('mypage.logout')}</span>
            </Button>
          </div>
          
          {/* Quick Actions */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-gray-100">
            <Button
              variant="outline"
              onClick={() => window.location.href = '/apply'}
              className="flex flex-col items-center gap-1 h-auto py-3 hover:bg-green-50 hover:border-green-300"
            >
              <FileText className="h-5 w-5 text-[#00A651]" />
              <span className="text-xs">{t('mypage.quickActions.apply')}</span>
            </Button>
            <Button
              variant="outline"
              onClick={() => { setActiveTab('applications'); setTimeout(() => tabsContentRef.current?.scrollIntoView({ behavior: 'smooth' }), 50); }}
              className="flex flex-col items-center gap-1 h-auto py-3 hover:bg-emerald-50 hover:border-emerald-300"
            >
              <History className="h-5 w-5 text-emerald-600" />
              <span className="text-xs">{t('mypage.quickActions.history')}</span>
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setActiveTab('profile');
                setEditMode(true);
                setTimeout(() => tabsContentRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
              }}
              className="flex flex-col items-center gap-1 h-auto py-3 hover:bg-blue-50 hover:border-blue-300"
            >
              <Edit className="h-5 w-5 text-blue-600" />
              <span className="text-xs">{t('mypage.quickActions.editProfile')}</span>
            </Button>
            <Button
              variant="outline"
              onClick={() => { setActiveTab('watching'); setTimeout(() => tabsContentRef.current?.scrollIntoView({ behavior: 'smooth' }), 50); }}
              className="flex flex-col items-center gap-1 h-auto py-3 hover:bg-orange-50 hover:border-orange-300"
            >
              <PlayCircle className="h-5 w-5 text-orange-600" />
              <span className="text-xs">{t('mypage.quickActions.inProgress')}</span>
            </Button>
            <Button
              variant="outline"
              onClick={() => setQualificationsDialog(true)}
              className="flex flex-col items-center gap-1 h-auto py-3 hover:bg-purple-50 hover:border-purple-300"
            >
              <Award className="h-5 w-5 text-purple-600" />
              <span className="text-xs">{t('mypage.quickActions.qualifications')}</span>
            </Button>
            <Button
              variant="outline"
              onClick={() => { setActiveTab('invoices'); setTimeout(() => tabsContentRef.current?.scrollIntoView({ behavior: 'smooth' }), 50); }}
              className="flex flex-col items-center gap-1 h-auto py-3 hover:bg-amber-50 hover:border-amber-300 relative"
            >
              <CreditCard className="h-5 w-5 text-amber-600" />
              <span className="text-xs">{t('mypage.quickActions.invoices')}</span>
              <div className="flex gap-1 mt-0.5">
                {userInvoices.filter(i => i.status === 'unpaid').length > 0 && (
                  <span className="text-[9px] px-1 py-0.5 rounded bg-red-100 text-red-700 font-bold animate-pulse">{t('mypage.invoices.unpaid')}: {userInvoices.filter(i => i.status === 'unpaid').length}</span>
                )}
                {userInvoices.filter(i => i.status === 'paid').length > 0 && (
                  <span className="text-[9px] px-1 py-0.5 rounded bg-green-100 text-green-700">{t('mypage.invoices.paid')}: {userInvoices.filter(i => i.status === 'paid').length}</span>
                )}
              </div>
            </Button>
            <Button
              variant="outline"
              onClick={() => { setActiveTab('guides'); setTimeout(() => tabsContentRef.current?.scrollIntoView({ behavior: 'smooth' }), 50); }}
              className="flex flex-col items-center gap-1 h-auto py-3 hover:bg-cyan-50 hover:border-cyan-300"
            >
              <FileText className="h-5 w-5 text-cyan-600" />
              <span className="text-xs">{t('mp.guides')}</span>
            </Button>
            <Button
              variant="outline"
              onClick={handleLogout}
              className="flex flex-col items-center gap-1 h-auto py-3 hover:bg-red-50 hover:border-red-300"
            >
              <LogOut className="h-5 w-5 text-red-600" />
              <span className="text-xs">{t('mypage.logout')}</span>
            </Button>
          </div>
          
          {/* Stats */}
          {!loading && applications.length > 0 && (
            <div className="grid grid-cols-3 gap-4 mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-gray-100">
              <div className="text-center">
                <p className="text-xl sm:text-2xl font-bold text-gray-900">{applications.length}</p>
                <p className="text-xs sm:text-sm text-gray-500">{t('mp.applicationCount')}</p>
              </div>
              <div className="text-center">
                <p className="text-xl sm:text-2xl font-bold text-[#00A651]">{completedCount}</p>
                <p className="text-xs sm:text-sm text-gray-500">{t('mypage.applications.passed')}</p>
              </div>
              <div 
                className="text-center cursor-pointer hover:bg-blue-50 rounded-lg p-2 transition-colors"
                onClick={() => setActiveTab('completed')}
                data-testid="practical-confirmed-card"
              >
                <p className="text-xl sm:text-2xl font-bold text-blue-600">
                  {practicalReservations.filter(r => r.status === 'approved').length}
                </p>
                <p className="text-xs sm:text-sm text-gray-500">{t('mp.practicalConfirmed')}</p>
              </div>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div ref={tabsContentRef}>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="bg-white rounded-lg shadow-sm border border-gray-200">
          <TabsList className="w-full border-b border-gray-200 rounded-t-lg p-0 h-auto grid grid-cols-5">
            <TabsTrigger 
              value="applications" 
              className="py-2 sm:py-3 rounded-none data-[state=active]:bg-[#e6f6ec] data-[state=active]:text-[#00A651] text-xs sm:text-sm px-1"
            >
              <FileText className="h-4 w-4 sm:mr-1" />
              <span className="hidden sm:inline">{t('mp.applications')}</span>
              <span className="sm:hidden">{t('mp.apply')}</span>
            </TabsTrigger>
            <TabsTrigger 
              value="watching" 
              className="py-2 sm:py-3 rounded-none data-[state=active]:bg-orange-50 data-[state=active]:text-orange-600 text-xs sm:text-sm px-1"
            >
              <PlayCircle className="h-4 w-4 sm:mr-1" />
              <span className="hidden sm:inline">{t('mp.watching')}</span>
              <span className="sm:hidden">{t('mp.watch')}</span>
            </TabsTrigger>
            <TabsTrigger 
              value="completed" 
              className="py-2 sm:py-3 rounded-none data-[state=active]:bg-[#e6f6ec] data-[state=active]:text-[#00A651] text-xs sm:text-sm px-1"
            >
              <Award className="h-4 w-4 sm:mr-1" />
              <span className="hidden sm:inline">{t('mp.completed')}</span>
              <span className="sm:hidden">{t('mp.done')}</span>
            </TabsTrigger>
            <TabsTrigger 
              value="profile" 
              className="py-2 sm:py-3 rounded-none data-[state=active]:bg-[#e6f6ec] data-[state=active]:text-[#00A651] text-xs sm:text-sm px-1"
            >
              <Settings className="h-4 w-4 sm:mr-1" />
              <span className="hidden sm:inline">{t('mypage.tabs.profile')}</span>
              <span className="sm:hidden">{t('mp.set')}</span>
            </TabsTrigger>
            <TabsTrigger 
              value="invoices" 
              className={`py-2 sm:py-3 rounded-none data-[state=active]:bg-[#e6f6ec] text-xs sm:text-sm px-1 relative ${unpaidCount > 0 ? 'text-red-600 data-[state=active]:text-red-600 font-semibold' : 'data-[state=active]:text-[#00A651]'}`}
              data-testid="tab-invoices"
            >
              <FileText className="h-4 w-4 sm:mr-1" />
              <span className="hidden sm:inline">{t('mypage.quickActions.invoices')}</span>
              <span className="sm:hidden">{t('mp.bill')}</span>
              {unpaidCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center animate-pulse shadow" data-testid="invoices-unpaid-badge">
                  {unpaidCount}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger 
              value="guides" 
              className="py-2 sm:py-3 rounded-none data-[state=active]:bg-[#e6f6ec] data-[state=active]:text-[#00A651] text-xs sm:text-sm px-1"
            >
              <FileText className="h-4 w-4 sm:mr-1" />
              <span className="hidden sm:inline">{t('mp.guides')}</span>
              <span className="sm:hidden">{t('mp.guide')}</span>
            </TabsTrigger>
          </TabsList>

          {/* Applications Tab */}
          <TabsContent value="applications" className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-gray-900">
                {t('mp.courseApplications')}
              </h2>
              <Link to="/apply">
                <Button className="bg-[#00A651] hover:bg-[#008a43]">
                  {t('mp.newApplication')}
                </Button>
              </Link>
            </div>

            {loading ? (
              <div className="text-center py-8">
                <Loader2 className="h-8 w-8 animate-spin mx-auto text-[#00A651]" />
              </div>
            ) : applications.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>{t('mp.noApplications')}</p>
                <Link to="/apply">
                  <Button className="mt-4 bg-[#00A651] hover:bg-[#008a43]">
                    {t('mp.applyCourse')}
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {applications.map((app) => (
                  <div 
                    key={app.id} 
                    className={`border rounded-lg p-4 transition-colors cursor-pointer ${
                      app.status === 'completed_passed' 
                        ? 'border-green-200 bg-green-50/50 hover:border-green-300' 
                        : app.status === 'completed_failed'
                        ? 'border-orange-200 bg-orange-50/50 hover:border-orange-300'
                        : 'border-gray-200 hover:border-[#00A651]/30'
                    }`}
                    onClick={() => setSelectedApplication(app)}
                    data-testid={`application-card-${app.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900">{app.courseName || app.courseId}</h3>
                        <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            {t('mypage.applications.appliedDate')} {formatDate(app.createdAt)}
                          </span>
                          {app.scheduleName && (
                            <span>{t('mp.schedule')} {app.scheduleName}</span>
                          )}
                        </div>
                        {/* 受講済み合格の場合は追加情報を表示 */}
                        {app.status === 'completed_passed' && (
                          <div className="mt-3 pt-3 border-t border-green-200">
                            <div className="flex flex-wrap items-center gap-4 text-sm">
                              {app.completedAt && (
                                <span className="text-green-700">
                                  {t('mypage.completedDate')} {formatDate(app.completedAt)}
                                </span>
                              )}
                              {app.certificateNumber && (
                                <span className="text-green-700 font-medium">
                                  {t('mp.certNo')} {app.certificateNumber}
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                        <p className="text-xs text-gray-400 mt-2">{t('mp.clickForDetails')}</p>
                      </div>
                      {getStatusBadge(app.status)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Watching Tab - On-demand courses in progress */}
          <TabsContent value="watching" className="p-6">
            <div className="mb-6">
              <h2 className="text-lg font-bold text-gray-900">
                {t('mp.coursesInProgress')}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {t('mp.onDemandProgress')}
              </p>
            </div>

            {loading ? (
              <div className="text-center py-8">
                <Loader2 className="h-8 w-8 animate-spin mx-auto text-[#00A651]" />
              </div>
            ) : (
              <div className="space-y-4">
                {/* Show on-demand/hybrid courses with confirmed + paid status */}
                {applications.filter(app => 
                  isActiveCourse(app) && !isCourseFullyCompleted(app)
                ).length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Video className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p>{t('mp.noCourses')}</p>
                    <Link to="/courses?delivery=on-demand">
                      <Button className="mt-4 bg-orange-500 hover:bg-orange-600">
                        <PlayCircle className="h-4 w-4 mr-2" />
                        {t('mp.browseOnDemand')}
                      </Button>
                    </Link>
                  </div>
                ) : (
                  applications.filter(app => 
                    isActiveCourse(app) && !isCourseFullyCompleted(app)
                  ).map((app) => {
                    // Calculate progress from courseProgress data
                    const progress = courseProgress[app.courseId];
                    let progressPercent = 0;
                    let completedVideos = 0;
                    let totalVideos = 0;
                    
                    if (progress && progress.videos && progress.videos.length > 0) {
                      totalVideos = progress.videos.length;
                      completedVideos = progress.videos.filter(v => v.completed).length;
                      progressPercent = Math.round((completedVideos / totalVideos) * 100);
                    }
                    
                    return (
                    <div 
                      key={app.id} 
                      className="border border-orange-200 bg-orange-50/50 rounded-lg p-4"
                    >
                      <div className="flex items-start gap-4">
                        <div className="bg-orange-100 p-2 rounded-full">
                          <PlayCircle className="h-6 w-6 text-orange-600" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-medium text-gray-900">{app.courseName || app.courseId}</h3>
                          <div className="mt-2 text-sm text-gray-600">
                            <span className="text-gray-500">{t('mypage.applications.appliedDate')}</span> {formatDate(app.createdAt)}
                          </div>
                          {/* Progress bar with actual data */}
                          <div className="mt-3">
                            <div className="flex justify-between text-xs text-gray-500 mb-1">
                              <span>
                                {t('mypage.courses.progress')}
                                {totalVideos > 0 && ` (${completedVideos}/${totalVideos})`}
                              </span>
                              <span>{progressPercent}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div 
                                className="bg-orange-500 h-2 rounded-full transition-all duration-300"
                                style={{ width: `${progressPercent}%` }}
                              ></div>
                            </div>
                          </div>
                        </div>
                        <Link to={`/watch/${app.courseId}`}>
                          <Button className="bg-orange-500 hover:bg-orange-600">
                            <PlayCircle className="h-4 w-4 mr-1" />
                            {t('mp.watchCourse')}
                          </Button>
                        </Link>
                      </div>
                    </div>
                  )})
                )}
              </div>
            )}
          </TabsContent>

          {/* Completed Tab */}
          <TabsContent value="completed" className="p-6">
            <div className="mb-6">
              <h2 className="text-lg font-bold text-gray-900">
                {t('mp.completedCourses')}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {t('mp.completedList')}
              </p>
            </div>

            {loading ? (
              <div className="text-center py-8">
                <Loader2 className="h-8 w-8 animate-spin mx-auto text-[#00A651]" />
              </div>
            ) : (completedCount === 0 && applications.filter(app => app.status === 'completed_failed').length === 0 && applications.filter(app => isCourseFullyCompleted(app)).length === 0) ? (
              <div className="text-center py-12 text-gray-500">
                <Award className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>{t('mp.noCompletedCourses')}</p>
                <Link to="/courses">
                  <Button className="mt-4 bg-[#00A651] hover:bg-[#008a43]">
                    {t('mp.browseCourses')}
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {/* On-demand courses fully completed (all, regardless of certificate status) */}
                {applications.filter(app => isCourseFullyCompleted(app)).map((app) => (
                  <div 
                    key={app.id} 
                    className="border border-green-200 bg-green-50/50 rounded-lg p-4"
                  >
                    <div className="flex items-start gap-4">
                      <div className="bg-green-100 p-2 rounded-full">
                        <Award className="h-6 w-6 text-green-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900">{app.courseName || app.courseId}</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2 text-sm">
                          <div className="text-gray-600">
                            <span className="text-gray-500">{t('mypage.applications.appliedDate')}</span> {formatDate(app.createdAt)}
                          </div>
                          <div className="text-green-700 font-medium">
                            🎉 {t('mp.allCompleted')}
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        <Badge className="bg-green-100 text-green-800">{t('mypage.courses.graduated')}</Badge>
                        
                        {/* Hybrid: Practical Training Status */}
                        {app.courseType === 'hybrid' && (() => {
                          const pStatus = getPracticalStatus(app.courseId);
                          if (!pStatus) {
                            return (
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-orange-300 text-orange-700 hover:bg-orange-50"
                                data-testid={`practical-reserve-${app.courseId}`}
                                onClick={() => {
                                  setPracticalForm({ courseId: app.courseId, preferredDate1: '', preferredDate2: '', preferredDate3: '', message: '' });
                                  setShowPracticalDialog(true);
                                }}
                              >
                                <Calendar className="h-3 w-3 mr-1" />
                                {t('mp.bookPractical')}
                              </Button>
                            );
                          }
                          if (pStatus.result === 'pass') {
                            return <Badge className="bg-green-100 text-green-700 text-xs">実技合格</Badge>;
                          }
                          if (pStatus.result === 'fail') {
                            return <Badge className="bg-red-100 text-red-700 text-xs">実技不合格</Badge>;
                          }
                          if (pStatus.status === 'approved') {
                            return <Badge className="bg-blue-100 text-blue-700 text-xs">実技日程確定: {pStatus.confirmedDate || ''}</Badge>;
                          }
                          return <Badge className="bg-yellow-100 text-yellow-700 text-xs">実技予約申請中</Badge>;
                        })()}

                        {/* Certificate buttons: only show if NOT hybrid OR if hybrid AND practical passed */}
                        {(app.courseType !== 'hybrid' || isPracticalPassed(app.courseId)) && (
                          <>
                        {/* Card Certificate Button */}
                        {getCertRequestStatus(app.courseId, 'card') ? (
                          <Badge className={`text-xs ${
                            getCertRequestStatus(app.courseId, 'card') === 'completed' 
                              ? 'bg-green-100 text-green-700' 
                              : getCertRequestStatus(app.courseId, 'card') === 'processing'
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            <CreditCard className="h-3 w-3 mr-1" />
                            {getCertRequestStatus(app.courseId, 'card') === 'completed' 
                              ? (t('mypage.certificates.issued'))
                              : getCertRequestStatus(app.courseId, 'card') === 'processing'
                                ? (t('mypage.processing'))
                                : (t('mypage.certificates.requested'))}
                          </Badge>
                        ) : (
                          <Button 
                            size="sm"
                            variant="outline"
                            className="border-purple-300 text-purple-700 hover:bg-purple-50"
                            onClick={() => openCertificateDialog(app, 'card')}
                          >
                            <CreditCard className="h-3 w-3 mr-1" />
                            {t('mypage.certificates.card')}
                          </Button>
                        )}
                        {/* Digital Certificate Button */}
                        {getCertRequestStatus(app.courseId, 'digital') ? (
                          <Badge className={`text-xs ${
                            getCertRequestStatus(app.courseId, 'digital') === 'completed' 
                              ? 'bg-green-100 text-green-700' 
                              : getCertRequestStatus(app.courseId, 'digital') === 'processing'
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            <FileText className="h-3 w-3 mr-1" />
                            {getCertRequestStatus(app.courseId, 'digital') === 'completed' 
                              ? (t('mypage.certificates.issued'))
                              : getCertRequestStatus(app.courseId, 'digital') === 'processing'
                                ? (t('mypage.processing'))
                                : (t('mypage.certificates.requested'))}
                          </Badge>
                        ) : (
                          <Button 
                            size="sm"
                            className="bg-[#00A651] hover:bg-[#008a43]"
                            onClick={() => openCertificateDialog(app, 'digital')}
                          >
                            <FileText className="h-3 w-3 mr-1" />
                            {t('mypage.certificates.digital')}
                          </Button>
                        )}
                          </>
                        )}
                        {/* Hybrid + practical not passed: progress stepper + reason why certificate cannot be requested yet */}
                        {app.courseType === 'hybrid' && !isPracticalPassed(app.courseId) && (() => {
                          const pStatus = getPracticalStatus(app.courseId);
                          const hasRes = !!pStatus;
                          const confirmed = !!(pStatus && (pStatus.status === 'approved' || pStatus.result));
                          const failed = !!(pStatus && pStatus.result === 'fail');
                          const steps = [
                            { label: t('mypage.certificates.stepReserve'), state: hasRes ? 'done' : 'active' },
                            { label: t('mypage.certificates.stepConfirm'), state: !hasRes ? 'pending' : (confirmed ? 'done' : 'active') },
                            { label: t('mypage.certificates.stepAttend'), state: failed ? 'fail' : (confirmed ? 'active' : 'pending') },
                          ];
                          const circleCls = { done: 'bg-green-500 text-white', active: 'bg-orange-500 text-white', fail: 'bg-red-500 text-white', pending: 'bg-gray-200 text-gray-500' };
                          const labelCls = { done: 'text-green-700', active: 'text-orange-700 font-medium', fail: 'text-red-700', pending: 'text-gray-400' };
                          let msg;
                          if (!pStatus) msg = t('mypage.certificates.needPracticalReserve');
                          else if (pStatus.result === 'fail') msg = t('mypage.certificates.practicalFailed');
                          else if (pStatus.status === 'approved') msg = t('mypage.certificates.practicalApproved');
                          else msg = t('mypage.certificates.practicalPending');
                          return (
                            <div className="w-full max-w-[260px] space-y-2">
                              {/* Progress stepper */}
                              <div data-testid={`practical-steps-${app.courseId}`} className="flex items-start">
                                {steps.map((s, i) => (
                                  <div key={i} className={`flex items-center ${i < steps.length - 1 ? 'flex-1' : ''}`}>
                                    <div className="flex flex-col items-center shrink-0" style={{ width: '56px' }}>
                                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${circleCls[s.state]}`}>
                                        {s.state === 'done' ? <Check className="h-3.5 w-3.5" /> : (s.state === 'fail' ? '!' : i + 1)}
                                      </div>
                                      <span className={`mt-1 text-[10px] leading-tight text-center ${labelCls[s.state]}`}>{s.label}</span>
                                    </div>
                                    {i < steps.length - 1 && (
                                      <div className={`h-0.5 flex-1 -mt-4 ${s.state === 'done' ? 'bg-green-400' : 'bg-gray-200'}`} />
                                    )}
                                  </div>
                                ))}
                              </div>
                              {/* Reason / next action */}
                              <div
                                data-testid={`cert-locked-note-${app.courseId}`}
                                className="flex items-start gap-1.5 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2"
                              >
                                <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                                <span>{msg}</span>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                ))}
                
                {/* Face-to-face courses completed (passed) */}
                {applications.filter(app => app.status === 'completed_passed').map((app) => (
                  <div 
                    key={app.id} 
                    className="border border-green-200 bg-green-50/50 rounded-lg p-4"
                  >
                    <div className="flex items-start gap-4">
                      <div className="bg-green-100 p-2 rounded-full">
                        <Award className="h-6 w-6 text-green-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900">{app.courseName || app.courseId}</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2 text-sm">
                          {app.completedAt && (
                            <div className="text-gray-600">
                              <span className="text-gray-500">{t('mypage.completedDate')}</span> {formatDate(app.completedAt)}
                            </div>
                          )}
                          {app.certificateNumber && (
                            <div className="text-green-700 font-medium">
                              <span className="text-gray-500">{t('mp.certNo')}</span> {app.certificateNumber}
                            </div>
                          )}
                        </div>
                      </div>
                      <Badge className="bg-green-100 text-green-800">{t('mypage.applications.passed')}</Badge>
                    </div>
                  </div>
                ))}

                {/* Face-to-face courses completed (failed) */}
                {applications.filter(app => app.status === 'completed_failed').map((app) => (
                  <div 
                    key={app.id} 
                    className="border border-orange-200 bg-orange-50/50 rounded-lg p-4"
                  >
                    <div className="flex items-start gap-4">
                      <div className="bg-orange-100 p-2 rounded-full">
                        <Award className="h-6 w-6 text-orange-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900">{app.courseName || app.courseId}</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2 text-sm">
                          {app.completedAt && (
                            <div className="text-gray-600">
                              <span className="text-gray-500">{t('mypage.courseDate')}</span> {formatDate(app.completedAt)}
                            </div>
                          )}
                        </div>
                      </div>
                      <Badge className="bg-orange-100 text-orange-800">{t('mypage.applications.failed')}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Certifications Tab */}
          {/* Profile Tab */}
          <TabsContent value="profile" className="p-4 sm:p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-gray-900">
                {t('mp.profileSettings')}
              </h2>
              {editMode ? (
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setEditMode(false)} size="sm">
                    <X className="h-4 w-4 sm:mr-2" />
                    <span className="hidden sm:inline">{t('mypage.profile.cancel')}</span>
                  </Button>
                  <Button className="bg-[#00A651] hover:bg-[#008a43]" onClick={handleSaveProfile} size="sm">
                    <Save className="h-4 w-4 sm:mr-2" />
                    <span className="hidden sm:inline">{t('mypage.profile.save')}</span>
                  </Button>
                </div>
              ) : (
                <Button variant="outline" onClick={() => setEditMode(true)} size="sm">
                  <Edit className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">{t('mypage.profile.edit')}</span>
                </Button>
              )}
            </div>

            <div className="space-y-8">
              {/* Profile Photo Section */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                  <Camera className="h-4 w-4" />
                  {t('mp.profilePhoto')}
                </h3>
                <div className="flex items-center gap-4">
                  <div className="relative group">
                    {user.profileImage ? (
                      <img 
                        src={`${BACKEND_URL}${user.profileImage}`}
                        alt={user.name}
                        className="h-20 w-20 rounded-full object-cover border-2 border-[#00A651]"
                      />
                    ) : (
                      <div className="h-20 w-20 bg-gray-200 rounded-full flex items-center justify-center">
                        <User className="h-10 w-10 text-gray-400" />
                      </div>
                    )}
                    <button
                      onClick={() => setShowFaceCamera(true)}
                      className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                    >
                      <Upload className="h-6 w-6 text-white" />
                    </button>
                  </div>
                  <div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      {t('mp.changePhoto')}
                    </Button>
                    <p className="text-xs text-gray-500 mt-1">
                      {t('mp.imageFormat')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Personal Info Section */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                  <User className="h-4 w-4" />
                  {t('mp.personalInfo')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs">{t('mypage.profile.name')} <span className="text-red-500">*</span></Label>
                    <Input value={editMode ? editData.name : user.name} onChange={(e) => setEditData({ ...editData, name: e.target.value })} disabled={!editMode} className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs">{t('mypage.profile.nameKana')}</Label>
                    <Input value={editMode ? editData.nameKana : (user.nameKana || '')} onChange={(e) => setEditData({ ...editData, nameKana: e.target.value })} disabled={!editMode} className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs">{t('mypage.profile.email')}</Label>
                    <Input value={user.email} disabled className="mt-1 bg-gray-100" />
                    <p className="text-xs text-gray-500 mt-1">{t('mp.cannotChange')}</p>
                  </div>
                  <div>
                    <Label className="text-xs">{t('mypage.profile.phone')}</Label>
                    <Input value={editMode ? editData.phone : (user.phone || '')} onChange={(e) => setEditData({ ...editData, phone: e.target.value })} disabled={!editMode} className="mt-1" placeholder="090-1234-5678" />
                  </div>
                  <div>
                    <Label className="text-xs">{t('mp.birthDate')}</Label>
                    <Input type="date" value={editMode ? editData.birthDate : (user.birthDate || '')} onChange={(e) => setEditData({ ...editData, birthDate: e.target.value })} disabled={!editMode} className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs">{t('mypage.profile.gender')}</Label>
                    {editMode ? (
                      <select value={editData.gender} onChange={(e) => setEditData({ ...editData, gender: e.target.value })}
                        className="mt-1 w-full h-10 px-3 rounded-md border border-gray-200 bg-white text-sm">
                        <option value="">{t('mp.select')}</option>
                        <option value="male">{t('mypage.profile.male')}</option>
                        <option value="female">{t('mypage.profile.female')}</option>
                        <option value="other">{t('mypage.other')}</option>
                      </select>
                    ) : (
                      <Input value={user.gender === 'male' ? t('mypage.profile.male') : user.gender === 'female' ? t('mypage.profile.female') : user.gender === 'other' ? t('mypage.other') : ''} disabled className="mt-1" />
                    )}
                  </div>
                  <div>
                    <Label className="text-xs">{t('mypage.profile.nationality')}</Label>
                    <div className="mt-1">
                      <NationalitySelect value={editMode ? editData.nationality : (user.nationality || '')} onChange={(val) => {
                        const langMap = {
                          'Japan': 'ja', '日本': 'ja',
                          'Brazil': 'pt', 'ブラジル': 'pt', 'Portugal': 'pt',
                          'United States': 'en', 'アメリカ': 'en', 'United Kingdom': 'en', 'Australia': 'en', 'Canada': 'en', 'Philippines': 'tl', 'フィリピン': 'tl',
                          'Vietnam': 'vi', 'ベトナム': 'vi',
                          'Indonesia': 'id', 'インドネシア': 'id',
                          'Spain': 'es', 'スペイン': 'es', 'Mexico': 'es', 'メキシコ': 'es', 'Peru': 'es', 'ペルー': 'es', 'Bolivia': 'es', 'ボリビア': 'es', 'Colombia': 'es', 'Argentina': 'es',
                          'China': 'zh', '中国': 'zh', 'Korea': 'ja', '韓国': 'ja',
                        };
                        const autoLang = langMap[val] || editData.language || '';
                        setEditData({ ...editData, nationality: val, language: autoLang });
                      }} disabled={!editMode} language={language} dataTestId="nationality-select" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">{t('mypage.profile.language')}</Label>
                    <select
                      value={editMode ? (editData.language || '') : (user.language || '')}
                      onChange={(e) => setEditData({ ...editData, language: e.target.value })}
                      disabled={!editMode}
                      className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm disabled:opacity-50 disabled:bg-gray-100"
                      data-testid="language-select"
                    >
                      <option value="">{t('mypage.profile.selectPlease')}</option>
                      <option value="ja">日本語</option>
                      <option value="en">English</option>
                      <option value="pt">Português</option>
                      <option value="es">Español</option>
                      <option value="vi">Tiếng Việt</option>
                      <option value="id">Bahasa Indonesia</option>
                      <option value="tl">Tagalog</option>
                      <option value="zh">中文</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs">{t('mypage.profile.postalCode')}</Label>
                    <Input value={editMode ? editData.postalCode : (user.postalCode || '')} onChange={(e) => setEditData({ ...editData, postalCode: e.target.value })} disabled={!editMode} className="mt-1" placeholder="123-4567" />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs">{t('mypage.profile.address')}</Label>
                    <Input value={editMode ? editData.address : (user.address || '')} onChange={(e) => setEditData({ ...editData, address: e.target.value })} disabled={!editMode} className="mt-1" />
                  </div>
                </div>
              </div>

              {/* Company Info Section */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  {t('mp.companyInfo')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label className="text-xs">{t('mp.companyName')}</Label>
                    <Input value={editMode ? editData.company : (user.company || '')} onChange={(e) => setEditData({ ...editData, company: e.target.value })} disabled={!editMode} className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs">{t('mypage.profile.department')}</Label>
                    <Input value={editMode ? editData.department : (user.department || '')} onChange={(e) => setEditData({ ...editData, department: e.target.value })} disabled={!editMode} className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs">{t('mp.position')}</Label>
                    <Input value={editMode ? editData.position : (user.position || '')} onChange={(e) => setEditData({ ...editData, position: e.target.value })} disabled={!editMode} className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs">{t('mp.companyPostalCode')}</Label>
                    <Input value={editMode ? editData.companyPostalCode : (user.companyPostalCode || '')} onChange={(e) => setEditData({ ...editData, companyPostalCode: e.target.value })} disabled={!editMode} className="mt-1" placeholder="100-0001" />
                  </div>
                  <div>
                    <Label className="text-xs">{t('mp.companyPhone')}</Label>
                    <Input value={editMode ? editData.companyPhone : (user.companyPhone || '')} onChange={(e) => setEditData({ ...editData, companyPhone: e.target.value })} disabled={!editMode} className="mt-1" placeholder="03-1234-5678" />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs">{t('mp.companyAddress')}</Label>
                    <Input value={editMode ? editData.companyAddress : (user.companyAddress || '')} onChange={(e) => setEditData({ ...editData, companyAddress: e.target.value })} disabled={!editMode} className="mt-1" />
                  </div>
                </div>
              </div>

              {/* ID Document Section */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                  <CreditCard className="h-4 w-4" />
                  {t('mypage.documents.idDocuments')}
                </h3>

                {/* Document type selector */}
                <div className="flex flex-wrap gap-2 mb-3">
                  {[
                    { value: 'residence_card', label: '在留カード', color: '#00A651' },
                    { value: 'drivers_license', label: '運転免許証', color: '#2563eb' },
                    { value: 'my_number_card', label: 'マイナンバーカード', color: '#7c3aed' },
                  ].map(dt => (
                    <button key={dt.value} type="button" onClick={() => setIdDocTypeMypage(dt.value)}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                        idDocTypeMypage === dt.value ? 'text-white border-transparent' : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
                      }`}
                      style={idDocTypeMypage === dt.value ? { backgroundColor: dt.color } : {}}>
                      {dt.label}
                    </button>
                  ))}
                </div>

                {!idDocTypeMypage ? (
                  <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center text-sm text-gray-400">
                    上記から書類の種類を選択してください
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    {/* Front */}
                    <div>
                      <Label className="text-xs mb-2 block">{t('mypage.documents.front')}</Label>
                      {user.idDocumentFront ? (
                        <div className="relative group">
                          <div onClick={() => setImageModal({ isOpen: true, src: `${BACKEND_URL}${user.idDocumentFront}`, alt: t('mp.idFront') })}
                            className="border rounded-lg overflow-hidden cursor-pointer hover:border-[#00A651] transition-colors"
                            style={{ borderColor: { residence_card: '#00A651', drivers_license: '#2563eb', my_number_card: '#7c3aed' }[idDocTypeMypage] || '#d1d5db' }}>
                            <img src={`${BACKEND_URL}${user.idDocumentFront}`} alt="ID Front" className="w-full h-28 object-cover" />
                          </div>
                          <button type="button" onClick={() => setMypageCameraTarget('idFront')}
                            disabled={uploadingDoc === 'idFront'}
                            className="mt-2 w-full flex items-center justify-center gap-1 py-1.5 text-xs border rounded transition-colors"
                            style={{ color: { residence_card: '#00A651', drivers_license: '#2563eb', my_number_card: '#7c3aed' }[idDocTypeMypage], borderColor: { residence_card: '#00A651', drivers_license: '#2563eb', my_number_card: '#7c3aed' }[idDocTypeMypage] }}>
                            {uploadingDoc === 'idFront' ? <Loader2 className="h-3 w-3 animate-spin" /> : <Camera className="h-3 w-3" />}
                            再撮影
                          </button>
                        </div>
                      ) : (
                        <div className="border-2 border-dashed border-gray-300 rounded-lg h-28 flex flex-col items-center justify-center">
                          {uploadingDoc === 'idFront' ? (
                            <Loader2 className="h-5 w-5 animate-spin" style={{ color: { residence_card: '#00A651', drivers_license: '#2563eb', my_number_card: '#7c3aed' }[idDocTypeMypage] }} />
                          ) : (
                            <button type="button" onClick={() => setMypageCameraTarget('idFront')}
                              className="flex flex-col items-center justify-center p-2 rounded-lg hover:bg-gray-100 transition-colors">
                              <Camera className="h-6 w-6" style={{ color: { residence_card: '#00A651', drivers_license: '#2563eb', my_number_card: '#7c3aed' }[idDocTypeMypage] }} />
                              <span className="text-[10px] text-gray-500 mt-1">撮影</span>
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                    {/* Back */}
                    <div>
                      <Label className="text-xs mb-2 block">{t('mypage.documents.back')}</Label>
                      {user.idDocumentBack ? (
                        <div className="relative group">
                          <div onClick={() => setImageModal({ isOpen: true, src: `${BACKEND_URL}${user.idDocumentBack}`, alt: t('mp.idBack') })}
                            className="border rounded-lg overflow-hidden cursor-pointer hover:border-[#00A651] transition-colors"
                            style={{ borderColor: { residence_card: '#00A651', drivers_license: '#2563eb', my_number_card: '#7c3aed' }[idDocTypeMypage] || '#d1d5db' }}>
                            <img src={`${BACKEND_URL}${user.idDocumentBack}`} alt="ID Back" className="w-full h-28 object-cover" />
                          </div>
                          <button type="button" onClick={() => setMypageCameraTarget('idBack')}
                            disabled={uploadingDoc === 'idBack'}
                            className="mt-2 w-full flex items-center justify-center gap-1 py-1.5 text-xs border rounded transition-colors"
                            style={{ color: { residence_card: '#00A651', drivers_license: '#2563eb', my_number_card: '#7c3aed' }[idDocTypeMypage], borderColor: { residence_card: '#00A651', drivers_license: '#2563eb', my_number_card: '#7c3aed' }[idDocTypeMypage] }}>
                            {uploadingDoc === 'idBack' ? <Loader2 className="h-3 w-3 animate-spin" /> : <Camera className="h-3 w-3" />}
                            再撮影
                          </button>
                        </div>
                      ) : (
                        <div className="border-2 border-dashed border-gray-300 rounded-lg h-28 flex flex-col items-center justify-center">
                          {uploadingDoc === 'idBack' ? (
                            <Loader2 className="h-5 w-5 animate-spin" style={{ color: { residence_card: '#00A651', drivers_license: '#2563eb', my_number_card: '#7c3aed' }[idDocTypeMypage] }} />
                          ) : (
                            <button type="button" onClick={() => setMypageCameraTarget('idBack')}
                              className="flex flex-col items-center justify-center p-2 rounded-lg hover:bg-gray-100 transition-colors">
                              <Camera className="h-6 w-6" style={{ color: { residence_card: '#00A651', drivers_license: '#2563eb', my_number_card: '#7c3aed' }[idDocTypeMypage] }} />
                              <span className="text-[10px] text-gray-500 mt-1">撮影</span>
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* ID Document History */}
                {(user.idDocumentFrontHistory?.length > 0 || user.idDocumentBackHistory?.length > 0) && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={() => setShowIdFrontHistory(!showIdFrontHistory)}
                      className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
                    >
                      <History className="h-4 w-4" />
                      {t('mp.showIdHistory')}
                      {showIdFrontHistory ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </button>
                    {showIdFrontHistory && (
                      <div className="mt-3">
                        <div className="grid grid-cols-2 gap-3">
                          {user.idDocumentFrontHistory?.length > 0 && (
                            <div>
                              <p className="text-xs text-gray-500 mb-2">{t('mypage.documents.frontHistory')}</p>
                              <div className="flex gap-2 flex-wrap">
                                {user.idDocumentFrontHistory.map((item, idx) => {
                                  const imgPath = (item.path || item.url || '').replace('/api', '');
                                  return (
                                  <div
                                    key={idx}
                                    onClick={() => setImageModal({ isOpen: true, src: `${BACKEND_URL}${imgPath}`, alt: `${t('mypage.idFrontHistory')} ${idx + 1}` })}
                                    className="block border border-gray-200 rounded overflow-hidden hover:border-gray-400 transition-colors cursor-pointer"
                                  >
                                    <img src={`${BACKEND_URL}${imgPath}`} alt={`ID Front ${idx + 1}`} className="w-20 h-16 object-cover" />
                                    <p className="text-[9px] text-gray-400 text-center py-0.5 bg-gray-50">{item.archivedAt?.split('T')[0]}</p>
                                  </div>
                                )})}
                              </div>
                            </div>
                          )}
                          {user.idDocumentBackHistory?.length > 0 && (
                            <div>
                              <p className="text-xs text-gray-500 mb-2">{t('mypage.documents.backHistory')}</p>
                              <div className="flex gap-2 flex-wrap">
                                {user.idDocumentBackHistory.map((item, idx) => {
                                  const imgPath = (item.path || item.url || '').replace('/api', '');
                                  return (
                                  <div
                                    key={idx}
                                    onClick={() => setImageModal({ isOpen: true, src: `${BACKEND_URL}${imgPath}`, alt: `${t('mypage.idBackHistory')} ${idx + 1}` })}
                                    className="block border border-gray-200 rounded overflow-hidden hover:border-gray-400 transition-colors cursor-pointer"
                                  >
                                    <img src={`${BACKEND_URL}${imgPath}`} alt={`ID Back ${idx + 1}`} className="w-20 h-16 object-cover" />
                                    <p className="text-[9px] text-gray-400 text-center py-0.5 bg-gray-50">{item.archivedAt?.split('T')[0]}</p>
                                  </div>
                                )})}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Qualification Certificate Section */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                  <Award className="h-4 w-4" />
                  {t('mypage.documents.certDocuments')}
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  {/* Front */}
                  <div>
                    <Label className="text-xs mb-2 block">{t('mypage.documents.front')}</Label>
                    {user.certDocumentFront && user.certDocumentFront.length > 0 ? (
                      <div className="relative group">
                        <div onClick={() => setImageModal({ isOpen: true, src: `${BACKEND_URL}${user.certDocumentFront}`, alt: t('mp.certFront') })}
                          className="border border-[#00A651] rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition">
                          <img src={`${BACKEND_URL}${user.certDocumentFront}`} alt="Cert Front" className="w-full h-28 object-cover" />
                        </div>
                        <button type="button" onClick={() => setMypageCameraTarget('certFront')}
                          disabled={uploadingDoc === 'certFront'}
                          className="mt-2 w-full flex items-center justify-center gap-1 py-1.5 text-xs text-[#00A651] border border-[#00A651] rounded transition-colors hover:bg-green-50">
                          {uploadingDoc === 'certFront' ? <Loader2 className="h-3 w-3 animate-spin" /> : <Camera className="h-3 w-3" />}
                          再撮影
                        </button>
                      </div>
                    ) : (
                      <div className="border-2 border-dashed border-gray-300 rounded-lg h-28 flex flex-col items-center justify-center">
                        {uploadingDoc === 'certFront' ? (
                          <Loader2 className="h-5 w-5 animate-spin text-[#00A651]" />
                        ) : (
                          <button type="button" onClick={() => setMypageCameraTarget('certFront')}
                            className="flex flex-col items-center justify-center p-2 rounded-lg hover:bg-gray-100 transition-colors">
                            <Camera className="h-6 w-6 text-[#00A651]" />
                            <span className="text-[10px] text-gray-500 mt-1">撮影</span>
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  {/* Back */}
                  <div>
                    <Label className="text-xs mb-2 block">{t('mypage.documents.back')}</Label>
                    {user.certDocumentBack && user.certDocumentBack.length > 0 ? (
                      <div className="relative group">
                        <div onClick={() => setImageModal({ isOpen: true, src: `${BACKEND_URL}${user.certDocumentBack}`, alt: t('mp.certBack') })}
                          className="border border-[#00A651] rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition">
                          <img src={`${BACKEND_URL}${user.certDocumentBack}`} alt="Cert Back" className="w-full h-28 object-cover" />
                        </div>
                        <button type="button" onClick={() => setMypageCameraTarget('certBack')}
                          disabled={uploadingDoc === 'certBack'}
                          className="mt-2 w-full flex items-center justify-center gap-1 py-1.5 text-xs text-[#00A651] border border-[#00A651] rounded transition-colors hover:bg-green-50">
                          {uploadingDoc === 'certBack' ? <Loader2 className="h-3 w-3 animate-spin" /> : <Camera className="h-3 w-3" />}
                          再撮影
                        </button>
                      </div>
                    ) : (
                      <div className="border-2 border-dashed border-gray-300 rounded-lg h-28 flex flex-col items-center justify-center">
                        {uploadingDoc === 'certBack' ? (
                          <Loader2 className="h-5 w-5 animate-spin text-[#00A651]" />
                        ) : (
                          <button type="button" onClick={() => setMypageCameraTarget('certBack')}
                            className="flex flex-col items-center justify-center p-2 rounded-lg hover:bg-gray-100 transition-colors">
                            <Camera className="h-6 w-6 text-[#00A651]" />
                            <span className="text-[10px] text-gray-500 mt-1">撮影</span>
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Qualification Certificate History */}
                {(user.certDocumentFrontHistory?.length > 0 || user.certDocumentBackHistory?.length > 0) && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={() => setShowCertFrontHistory(!showCertFrontHistory)}
                      className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
                    >
                      <History className="h-4 w-4" />
                      {t('mp.showCertHistory')}
                      {showCertFrontHistory ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </button>
                    {showCertFrontHistory && (
                      <div className="mt-3">
                        <div className="grid grid-cols-2 gap-3">
                          {user.certDocumentFrontHistory?.length > 0 && (
                            <div>
                              <p className="text-xs text-gray-500 mb-2">{t('mypage.documents.frontHistory')}</p>
                              <div className="flex gap-2 flex-wrap">
                                {user.certDocumentFrontHistory.map((item, idx) => {
                                  const imgPath = (item.path || item.url || '').replace('/api', '');
                                  return (
                                  <div key={idx}
                                    onClick={() => setImageModal({ isOpen: true, src: `${BACKEND_URL}${imgPath}`, alt: `${t('mypage.certFrontHistory')} ${idx + 1}` })}
                                    className="block border border-gray-200 rounded overflow-hidden hover:border-gray-400 transition-colors cursor-pointer">
                                    <img src={`${BACKEND_URL}${imgPath}`} alt={`Cert Front ${idx + 1}`} className="w-20 h-16 object-cover" />
                                    <p className="text-[9px] text-gray-400 text-center py-0.5 bg-gray-50">{item.archivedAt?.split('T')[0]}</p>
                                  </div>
                                )})}
                              </div>
                            </div>
                          )}
                          {user.certDocumentBackHistory?.length > 0 && (
                            <div>
                              <p className="text-xs text-gray-500 mb-2">{t('mypage.documents.backHistory')}</p>
                              <div className="flex gap-2 flex-wrap">
                                {user.certDocumentBackHistory.map((item, idx) => {
                                  const imgPath = (item.path || item.url || '').replace('/api', '');
                                  return (
                                  <div key={idx}
                                    onClick={() => setImageModal({ isOpen: true, src: `${BACKEND_URL}${imgPath}`, alt: `${t('mypage.certBackHistory')} ${idx + 1}` })}
                                    className="block border border-gray-200 rounded overflow-hidden hover:border-gray-400 transition-colors cursor-pointer">
                                    <img src={`${BACKEND_URL}${imgPath}`} alt={`Cert Back ${idx + 1}`} className="w-20 h-16 object-cover" />
                                    <p className="text-[9px] text-gray-400 text-center py-0.5 bg-gray-50">{item.archivedAt?.split('T')[0]}</p>
                                  </div>
                                )})}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Account Info */}
              <div className="pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-500">
                  {t('mp.memberSince')} {formatDate(user.createdAt)}
                </p>
              </div>
            </div>
          </TabsContent>

          {/* Invoices Tab */}
          <TabsContent value="invoices" className="p-6">
            <h3 className="text-lg font-bold text-gray-800 mb-4">
              {t('mypage.invoices.title')}
            </h3>
            {userInvoices.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>{t('mypage.invoices.noInvoices')}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {userInvoices.map(inv => (
                  <div key={inv.id} className="border rounded-lg p-4" data-testid={`user-invoice-${inv.id}`}>
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-mono text-sm text-gray-500">{inv.invoiceNumber}</span>
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                        inv.status === 'paid' ? 'bg-green-100 text-green-700' :
                        inv.status === 'cancelled' ? 'bg-gray-100 text-gray-500' :
                        'bg-orange-100 text-orange-700'
                      }`}>
                        {inv.status === 'paid' ? (t('mypage.invoices.paid')) : 
                         inv.status === 'cancelled' ? (t('mypage.cancelled')) : 
                         (t('mypage.invoices.unpaid'))}
                      </span>
                    </div>
                    {inv.courseName && <p className="text-sm text-gray-600 mb-2">{inv.courseName}</p>}
                    <div className="bg-gray-50 rounded p-3 space-y-1">
                      {inv.items?.map((item, idx) => (
                        <div key={idx} className="flex justify-between text-sm">
                          <span>{item.name}</span>
                          <span>¥{(item.amount || 0).toLocaleString()}</span>
                        </div>
                      ))}
                      <div className="border-t pt-2 mt-2 flex justify-between font-bold">
                        <span>{t('mp.total')}</span>
                        <span className="text-[#00A651]">¥{(inv.totalAmount || 0).toLocaleString()}</span>
                      </div>
                    </div>
                    {inv.note && <p className="text-xs text-gray-500 mt-2">{t('mp.noteLabel')} {inv.note}</p>}
                    {inv.status === 'unpaid' && (() => {
                      const selected = invMethod[inv.id] || inv.paymentMethod || '';
                      const setSel = (m) => setInvMethod(prev => ({ ...prev, [inv.id]: m }));
                      return (
                        <div className="mt-4 border-t pt-3" data-testid={`payment-section-${inv.id}`}>
                          <p className="text-sm font-medium text-gray-800 mb-2">{t('mp.pay.selectMethod')}</p>
                          <div className="grid grid-cols-3 gap-2">
                            {[
                              { key: 'bank_transfer', label: t('mp.pay.bankTransfer'), Icon: Building2 },
                              { key: 'cash', label: t('mp.pay.cash'), Icon: Banknote },
                              { key: 'credit_card', label: t('mp.pay.creditCard'), Icon: CreditCard },
                            ].map(({ key, label, Icon }) => (
                              <button
                                key={key}
                                type="button"
                                onClick={() => setSel(key)}
                                data-testid={`pay-method-${key}-${inv.id}`}
                                className={`flex flex-col items-center gap-1 p-2 rounded-lg border text-xs transition-colors ${selected === key ? 'border-[#00A651] bg-[#e6f6ec] text-[#00A651] font-medium' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}
                              >
                                <Icon className="h-5 w-5" />
                                {label}
                              </button>
                            ))}
                          </div>

                          {(selected === 'bank_transfer' || selected === 'cash') && (
                            <div className="mt-3 space-y-3">
                              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer" data-testid={`receipt-check-label-${inv.id}`}>
                                <input
                                  type="checkbox"
                                  className="h-4 w-4 accent-[#00A651]"
                                  checked={!!invReceipt[inv.id]}
                                  onChange={(e) => setInvReceipt(prev => ({ ...prev, [inv.id]: e.target.checked }))}
                                  data-testid={`receipt-check-${inv.id}`}
                                />
                                <Receipt className="h-4 w-4 text-gray-500" />
                                {t('mp.pay.receiptRequest')}
                              </label>
                              {selected === 'bank_transfer' && Object.keys(bankInfo).length > 0 && (
                                <div className="bg-yellow-50 border border-yellow-200 rounded p-3 text-sm text-yellow-800 space-y-1">
                                  {bankInfo.bankName && <div>{t('mypage.bank')} {bankInfo.bankName}</div>}
                                  {bankInfo.bankBranch && <div>{t('mp.branch')} {bankInfo.bankBranch}</div>}
                                  {bankInfo.bankAccountType && <div>{t('mp.accountType')} {bankInfo.bankAccountType}</div>}
                                  {bankInfo.bankAccountNumber && <div>{t('mp.accountNo')} {bankInfo.bankAccountNumber}</div>}
                                  {bankInfo.bankAccountHolder && <div>{t('mp.accountHolder')} {bankInfo.bankAccountHolder}</div>}
                                </div>
                              )}
                              <Button
                                size="sm"
                                disabled={payLoading}
                                onClick={() => handleSelectOfflineMethod(inv, selected)}
                                className="bg-[#00A651] hover:bg-[#008a43] text-white"
                                data-testid={`confirm-offline-method-${inv.id}`}
                              >
                                {payLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                                {t('mp.pay.confirmMethod')}
                              </Button>
                            </div>
                          )}

                          {selected === 'credit_card' && (
                            <div className="mt-3">
                              <p className="text-xs text-gray-500 mb-2">{t('mp.pay.cardNote')}</p>
                              <Button
                                size="sm"
                                disabled={payLoading}
                                onClick={() => handleStripePay(inv)}
                                className="bg-[#635bff] hover:bg-[#5249e0] text-white"
                                data-testid={`stripe-pay-${inv.id}`}
                              >
                                {payLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CreditCard className="h-4 w-4 mr-1" />}
                                {t('mp.pay.payByCard')} ¥{(inv.totalAmount || 0).toLocaleString()}
                              </Button>
                            </div>
                          )}

                          {inv.paymentMethod && (
                            <p className="text-xs text-gray-500 mt-2" data-testid={`chosen-method-${inv.id}`}>
                              {t('mp.pay.chosen')} {inv.paymentMethod === 'bank_transfer' ? t('mp.pay.bankTransfer') : inv.paymentMethod === 'cash' ? t('mp.pay.cash') : t('mp.pay.creditCard')}
                              {inv.receiptRequested ? `（${t('mp.pay.receiptRequest')}）` : ''}
                            </p>
                          )}
                        </div>
                      );
                    })()}
                    <div className="flex items-center justify-between mt-3">
                      <div className="text-xs text-gray-400">
                        {t('mypage.certificates.issueDate')} {new Date(inv.createdAt).toLocaleDateString('ja-JP')}
                        {inv.paidAt && <span className="ml-3">{t('mp.paidDate')} {new Date(inv.paidAt).toLocaleDateString('ja-JP')}</span>}
                      </div>
                      <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-[#00A651] border-[#00A651] hover:bg-[#00A651] hover:text-white transition-colors"
                        data-testid={`download-invoice-pdf-${inv.id}`}
                        onClick={async () => {
                          try {
                            const token = localStorage.getItem('userToken');
                            const res = await fetch(`${API}/invoices/${inv.id}/pdf`, {
                              headers: { Authorization: `Bearer ${token}` }
                            });
                            if (!res.ok) throw new Error('PDF generation failed');
                            const blob = await res.blob();
                            const url = window.URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `請求書_${inv.invoiceNumber}.pdf`;
                            document.body.appendChild(a);
                            a.click();
                            a.remove();
                            window.URL.revokeObjectURL(url);
                            toast.success('PDFをダウンロードしました');
                          } catch (err) {
                            console.error('Invoice PDF download error:', err);
                            toast.error('PDFダウンロードに失敗しました');
                          }
                        }}
                      >
                        <Download className="h-4 w-4 mr-1" />
                        PDF
                      </Button>
                      {inv.status === 'paid' && inv.receiptRequested && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="ml-2 text-[#635bff] border-[#635bff] hover:bg-[#635bff] hover:text-white transition-colors"
                          data-testid={`download-receipt-pdf-${inv.id}`}
                          onClick={async () => {
                            try {
                              const token = localStorage.getItem('userToken');
                              const res = await fetch(`${API}/invoices/${inv.id}/receipt-pdf`, {
                                headers: { Authorization: `Bearer ${token}` }
                              });
                              if (!res.ok) throw new Error('Receipt PDF generation failed');
                              const blob = await res.blob();
                              const url = window.URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = `領収書_${inv.invoiceNumber}.pdf`;
                              document.body.appendChild(a);
                              a.click();
                              a.remove();
                              window.URL.revokeObjectURL(url);
                              toast.success(t('mp.receiptDownloaded'));
                            } catch (err) {
                              console.error('Receipt PDF download error:', err);
                              toast.error(t('mp.receiptFailed'));
                            }
                          }}
                        >
                          <Receipt className="h-4 w-4 mr-1" />
                          {t('mp.receipt')}
                        </Button>
                      )}
                      </div>
                    </div>
                  </div>
                ))}

                {/* Bank Info */}
                {Object.keys(bankInfo).length > 0 && userInvoices.some(i => i.status === 'unpaid') && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
                    <h4 className="font-medium text-yellow-800 mb-2">{t('mp.bankInfo')}</h4>
                    <div className="text-sm text-yellow-700 space-y-1">
                      {bankInfo.bankName && <div>{t('mypage.bank')} {bankInfo.bankName}</div>}
                      {bankInfo.bankBranch && <div>{t('mp.branch')} {bankInfo.bankBranch}</div>}
                      {bankInfo.bankAccountType && <div>{t('mp.accountType')} {bankInfo.bankAccountType}</div>}
                      {bankInfo.bankAccountNumber && <div>{t('mp.accountNo')} {bankInfo.bankAccountNumber}</div>}
                      {bankInfo.bankAccountHolder && <div>{t('mp.accountHolder')} {bankInfo.bankAccountHolder}</div>}
                    </div>
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          {/* Guides Tab */}
          <TabsContent value="guides" className="p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4">
              {t('mp.trainingGuides')}
            </h2>
            {userGuides.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>{t('mp.noGuides')}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {userGuides.map(guide => (
                  <div key={guide.id} className="border rounded-lg overflow-hidden">
                    <div className="bg-[#00A651] text-white px-4 py-3 flex items-center justify-between">
                      <h3 className="font-medium">{guide.courseName}</h3>
                      <Badge className="bg-white/20 text-white">
                        {guide.status === 'sent' ? (t('mp.sent')) : (t('mp.created'))}
                      </Badge>
                    </div>
                    <div className="p-4 space-y-3">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                        {guide.schedule && (
                          <div>
                            <span className="text-xs text-gray-500 font-medium">{t('mp.schedule')}</span>
                            <p className="text-gray-900">{guide.schedule}</p>
                          </div>
                        )}
                        {guide.meetingTime && (
                          <div>
                            <span className="text-xs text-gray-500 font-medium">{t('mp.meetingTime')}</span>
                            <p className="text-gray-900">{guide.meetingTime}</p>
                          </div>
                        )}
                        {guide.venue && (
                          <div>
                            <span className="text-xs text-gray-500 font-medium">{t('mp.venue')}</span>
                            <p className="text-gray-900">{guide.venue}</p>
                            {guide.venueAddress && <p className="text-xs text-gray-500">{guide.venueAddress}</p>}
                          </div>
                        )}
                      </div>
                      {guide.itemsToBring && (
                        <div className="text-sm">
                          <span className="text-xs text-gray-500 font-medium">{t('mp.itemsToBring')}</span>
                          <p className="text-gray-900 whitespace-pre-line">{guide.itemsToBring}</p>
                        </div>
                      )}
                      {guide.notes && (
                        <div className="text-sm">
                          <span className="text-xs text-gray-500 font-medium">{t('mypage.notes')}</span>
                          <p className="text-gray-900 whitespace-pre-line">{guide.notes}</p>
                        </div>
                      )}
                      {guide.mapUrl && (
                        <div className="text-sm">
                          <span className="text-xs text-gray-500 font-medium">{t('mp.map')}</span>
                          <a href={guide.mapUrl} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline block">{guide.mapUrl}</a>
                        </div>
                      )}
                      {guide.additionalInfo && (
                        <div className="text-sm">
                          <span className="text-xs text-gray-500 font-medium">{t('mypage.other')}</span>
                          <p className="text-gray-900 whitespace-pre-line">{guide.additionalInfo}</p>
                        </div>
                      )}
                      <div className="pt-2 border-t flex justify-end">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(`${API}/admin/training-guides/${guide.id}/pdf`, '_blank')}
                        >
                          <FileText className="h-4 w-4 mr-1" />
                          {t('mp.viewPdf')}
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
        </div>      </div>
      
      {/* Image Modal */}
      {imageModal.isOpen && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
          onClick={() => setImageModal({ isOpen: false, src: '', alt: '' })}
        >
          <div className="relative max-w-4xl max-h-[90vh] m-4">
            <button
              onClick={() => setImageModal({ isOpen: false, src: '', alt: '' })}
              className="absolute -top-10 right-0 text-white hover:text-gray-300 transition-colors"
            >
              <X className="h-8 w-8" />
            </button>
            <img 
              src={imageModal.src} 
              alt={imageModal.alt} 
              className="max-w-full max-h-[85vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
            <p className="text-white text-center mt-2 text-sm">{imageModal.alt}</p>
          </div>
        </div>
      )}

      {/* Certificate Request Dialog */}
      <Dialog open={showCertDialog} onOpenChange={setShowCertDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {certRequestType === 'card' ? (
                <CreditCard className="h-5 w-5 text-purple-600" />
              ) : (
                <FileText className="h-5 w-5 text-[#00A651]" />
              )}
              {certRequestType === 'card'
                ? (t('mypage.cardCertRequest'))
                : (t('mypage.digitalCertRequest'))}
            </DialogTitle>
            <DialogDescription>
              {certRequestType === 'card'
                ? t('courseWatch.certTypeCardDesc')
                : t('courseWatch.certTypeDigitalDesc')}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-4">
            <div className="bg-gray-50 p-4 rounded-lg space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">{t('mypage.certificates.courseName')}</span>
                <span className="font-medium">{certRequestCourse?.courseName || certRequestCourse?.courseId}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">{t('mp.participantName')}</span>
                <span className="font-medium">{user?.name}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">{t('mp.certType')}</span>
                <span className="font-medium">
                  {certRequestType === 'card' 
                    ? (t('mypage.certificates.card')) 
                    : (t('mypage.certificates.digital'))}
                </span>
              </div>
            </div>

            {certRequestType === 'card' && (
              <div className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">
                <p className="font-medium">{t('mp.note')}</p>
                <p>{t('mp.cardCertNote')}</p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCertDialog(false)}>
              {t('mypage.profile.cancel')}
            </Button>
            <Button 
              onClick={submitCertificateRequest}
              disabled={submittingCertRequest}
              className={certRequestType === 'card' ? 'bg-purple-600 hover:bg-purple-700' : 'bg-[#00A651] hover:bg-[#008a43]'}
            >
              {submittingCertRequest ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t('mypage.submitting')}</>
              ) : (
                <><Send className="h-4 w-4 mr-2" /> {t('mypage.submitCertRequest')}</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Application Detail Dialog */}
      <Dialog open={!!selectedApplication} onOpenChange={(open) => !open && setSelectedApplication(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('mypage.applicationDetails')}</DialogTitle>
          </DialogHeader>
          {selectedApplication && (
            <div className="space-y-4" data-testid="application-detail-dialog">
              {/* Status */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">{t('mypage.certificates.status')}</span>
                {getStatusBadge(selectedApplication.status)}
              </div>

              {/* Course Info */}
              <div className="border rounded-lg p-4 bg-gray-50 space-y-3">
                <div>
                  <span className="text-xs text-gray-500">{t('mypage.certificates.courseName')}</span>
                  <p className="font-medium">{selectedApplication.courseName || selectedApplication.courseId}</p>
                </div>
                {selectedApplication.scheduleName && (
                  <div>
                    <span className="text-xs text-gray-500">{t('mp.courseSchedule')}</span>
                    <p className="text-sm">{selectedApplication.scheduleName}</p>
                  </div>
                )}
                <div>
                  <span className="text-xs text-gray-500">{t('mp.applied')}</span>
                  <p className="text-sm">{formatDate(selectedApplication.createdAt)}</p>
                </div>
              </div>

              {/* Applicant Info */}
              <div className="border rounded-lg p-4 space-y-3">
                <h4 className="text-sm font-medium text-gray-700">{t('mp.applicantInfo')}</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-xs text-gray-500">{t('mypage.profile.name')}</span>
                    <p>{selectedApplication.name || '-'}</p>
                  </div>
                  <div>
                    <span className="text-xs text-gray-500">{t('mypage.profile.phone')}</span>
                    <p>{selectedApplication.phone || '-'}</p>
                  </div>
                  <div>
                    <span className="text-xs text-gray-500">{t('mp.email')}</span>
                    <p>{selectedApplication.email || '-'}</p>
                  </div>
                  <div>
                    <span className="text-xs text-gray-500">{t('mypage.profile.company')}</span>
                    <p>{selectedApplication.company || '-'}</p>
                  </div>
                </div>
                {selectedApplication.experience && (
                  <div className="text-sm">
                    <span className="text-xs text-gray-500">{t('mp.experience')}</span>
                    <p>{selectedApplication.experience === 'yes' ? (t('mp.yes')) : (t('mp.no'))}</p>
                  </div>
                )}
                {selectedApplication.message && (
                  <div className="text-sm">
                    <span className="text-xs text-gray-500">{t('mp.notesQuestions')}</span>
                    <p className="text-gray-700">{selectedApplication.message}</p>
                  </div>
                )}
              </div>

              {/* Completion Info */}
              {selectedApplication.status === 'completed' && (
                <div className="border rounded-lg p-4 bg-green-50 space-y-2">
                  <h4 className="text-sm font-medium text-green-800">{t('mp.completionInfo')}</h4>
                  {selectedApplication.completedAt && (
                    <div className="text-sm">
                      <span className="text-xs text-green-600">{t('mp.completionDate')}</span>
                      <p className="text-green-800">{formatDate(selectedApplication.completedAt)}</p>
                    </div>
                  )}
                  {selectedApplication.certificateNumber && (
                    <div className="text-sm">
                      <span className="text-xs text-green-600">{t('mp.certNumber')}</span>
                      <p className="font-mono font-medium text-green-800">{selectedApplication.certificateNumber}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Participants */}
              {selectedApplication.participants && selectedApplication.participants.length > 0 && (
                <div className="border rounded-lg p-4 space-y-3">
                  <h4 className="text-sm font-medium text-gray-700">
                    {`${t('mp.participantInfo')}（${selectedApplication.participants.length}）`}
                  </h4>
                  {selectedApplication.participants.map((p, idx) => (
                    <div key={idx} className="p-2 bg-gray-50 rounded text-sm space-y-1">
                      <div className="flex items-center gap-3">
                        <span className="text-xs font-medium text-[#00A651] bg-green-100 px-2 py-0.5 rounded-full">{idx + 1}</span>
                        <span className="font-medium">{p.name || '-'}</span>
                        <div className="flex gap-1 ml-auto flex-wrap">
                          {p.idDocumentFront && (
                            <a href={`${BACKEND_URL}${p.idDocumentFront}`} target="_blank" rel="noreferrer">
                              <Badge variant="outline" className="text-xs cursor-pointer hover:bg-gray-100">{t('mypage.id')}(表)</Badge>
                            </a>
                          )}
                          {p.idDocumentBack && (
                            <a href={`${BACKEND_URL}${p.idDocumentBack}`} target="_blank" rel="noreferrer">
                              <Badge variant="outline" className="text-xs cursor-pointer hover:bg-gray-100">{t('mypage.id')}(裏)</Badge>
                            </a>
                          )}
                          {p.idDocumentPhoto && (
                            <a href={`${BACKEND_URL}${p.idDocumentPhoto}`} target="_blank" rel="noreferrer">
                              <Badge variant="outline" className="text-xs cursor-pointer hover:bg-gray-100">{t('mypage.id')}</Badge>
                            </a>
                          )}
                          {p.qualificationFront && (
                            <a href={`${BACKEND_URL}${p.qualificationFront}`} target="_blank" rel="noreferrer">
                              <Badge variant="outline" className="text-xs cursor-pointer hover:bg-gray-100">{t('mypage.cert')}(表)</Badge>
                            </a>
                          )}
                          {p.qualificationBack && (
                            <a href={`${BACKEND_URL}${p.qualificationBack}`} target="_blank" rel="noreferrer">
                              <Badge variant="outline" className="text-xs cursor-pointer hover:bg-gray-100">{t('mypage.cert')}(裏)</Badge>
                            </a>
                          )}
                          {p.qualificationPhoto && (
                            <a href={`${BACKEND_URL}${p.qualificationPhoto}`} target="_blank" rel="noreferrer">
                              <Badge variant="outline" className="text-xs cursor-pointer hover:bg-gray-100">{t('mypage.cert')}</Badge>
                            </a>
                          )}
                        </div>
                      </div>
                      {(p.birthDate || p.gender || p.nationality || p.postalCode || p.address) && (
                        <div className="ml-8 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-gray-500">
                          {p.birthDate && <span>{p.birthDate}</span>}
                          {p.gender && <span>{p.gender === 'male' ? t('mypage.profile.male') : p.gender === 'female' ? t('mypage.profile.female') : p.gender === 'other' ? t('mypage.other') : p.gender}</span>}
                          {p.nationality && <span>{p.nationality}</span>}
                          {p.postalCode && <span>{p.postalCode}</span>}
                          {p.address && <span>{p.address}</span>}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Related Invoices */}
              {(() => {
                const relatedInvoices = userInvoices.filter(inv => inv.applicationId === selectedApplication.id);
                if (relatedInvoices.length === 0) return null;
                return (
                  <div className="border rounded-lg p-4 space-y-2">
                    <h4 className="text-sm font-medium text-gray-700">{t('mp.invoiceInfo')}</h4>
                    {relatedInvoices.map(inv => (
                      <div key={inv.id} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                        <div>
                          <span className="font-mono text-xs text-gray-500">{inv.invoiceNumber}</span>
                          <span className="ml-2 font-medium">¥{(inv.totalAmount || 0).toLocaleString()}</span>
                        </div>
                        <Badge className={inv.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}>
                          {inv.status === 'paid' ? (t('mypage.invoices.paid')) : (t('mypage.invoices.unpaid'))}
                        </Badge>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedApplication(null)}>
              {t('mypage.profile.close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Practical Training Reservation Dialog */}
      <Dialog open={showPracticalDialog} onOpenChange={setShowPracticalDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('mp.practicalReservation')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              {t('mp.enterDates')}
            </p>
            <div>
              <label className="text-sm font-medium">{t('mp.prefDate1')} *</label>
              <input
                type="date"
                className="w-full border rounded px-3 py-2 mt-1 text-sm"
                data-testid="practical-date1"
                value={practicalForm.preferredDate1}
                onChange={e => setPracticalForm(f => ({ ...f, preferredDate1: e.target.value }))}
              />
            </div>
            <div>
              <label className="text-sm font-medium">{t('mp.prefDate2')}</label>
              <input
                type="date"
                className="w-full border rounded px-3 py-2 mt-1 text-sm"
                data-testid="practical-date2"
                value={practicalForm.preferredDate2}
                onChange={e => setPracticalForm(f => ({ ...f, preferredDate2: e.target.value }))}
              />
            </div>
            <div>
              <label className="text-sm font-medium">{t('mp.prefDate3')}</label>
              <input
                type="date"
                className="w-full border rounded px-3 py-2 mt-1 text-sm"
                data-testid="practical-date3"
                value={practicalForm.preferredDate3}
                onChange={e => setPracticalForm(f => ({ ...f, preferredDate3: e.target.value }))}
              />
            </div>
            <div>
              <label className="text-sm font-medium">{t('mp.messageOptional')}</label>
              <textarea
                className="w-full border rounded px-3 py-2 mt-1 text-sm min-h-[80px]"
                data-testid="practical-message"
                value={practicalForm.message}
                onChange={e => setPracticalForm(f => ({ ...f, message: e.target.value }))}
                placeholder={t('mp.specialRequests')}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPracticalDialog(false)}>
              {t('mypage.profile.cancel')}
            </Button>
            <Button
              className="bg-[#00A651] hover:bg-[#008a43]"
              data-testid="submit-practical-btn"
              disabled={!practicalForm.preferredDate1}
              onClick={submitPracticalReservation}
            >
              {t('mp.submit')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Certificate View Dialog */}
      <Dialog open={viewCertDialog} onOpenChange={(open) => { setViewCertDialog(open); if (!open) { setViewCertData(null); setCertImageUrl(null); } }}>
        <DialogContent className="max-w-2xl p-4">
          <DialogHeader className="sr-only">
            <DialogTitle>修了証</DialogTitle>
            <DialogDescription>証明書表示</DialogDescription>
          </DialogHeader>
          {certImageLoading && (
            <div className="flex justify-center py-16">
              <div className="animate-spin h-10 w-10 border-4 border-[#00A651] border-t-transparent rounded-full" />
            </div>
          )}
          {certImageUrl && (
            <img src={certImageUrl} alt="修了証" className="w-full rounded shadow-lg" />
          )}
          {!certImageLoading && !certImageUrl && viewCertData && (
            <div className="text-center py-8 text-red-500">証明書の読み込みに失敗しました</div>
          )}
          <div className="flex justify-center gap-3 mt-3">
            <Button variant="outline" size="sm" onClick={() => setViewCertDialog(false)}>{t('mypage.profile.close')}</Button>
            <Button size="sm" className="bg-[#00A651] hover:bg-[#008a43] text-white" onClick={async () => {
              try {
                const certId = viewCertData?.cert?.id;
                const endpoint = certId
                  ? `${API}/users/me/certificate-requests/${certId}/pdf`
                  : `${API}/users/me/certificates/${viewCertData?.app?.id}/pdf`;
                const res = await axios.get(endpoint, { headers: getAuthHeaders(), responseType: 'blob' });
                const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                window.open(url, '_blank');
              } catch { toast.error('PDF生成に失敗しました'); }
            }}>
              <Download className="h-4 w-4 mr-1" />
              PDFダウンロード
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Profile Incomplete Dialog */}
      <Dialog open={profileIncompleteDialog} onOpenChange={setProfileIncompleteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="h-5 w-5" />
              {t('mypage.profile.incompleteTitle')}
            </DialogTitle>
            <DialogDescription>{t('mypage.profile.incompleteDescription')}</DialogDescription>
          </DialogHeader>
          <div className="py-3">
            <p className="text-sm text-gray-600 mb-3">{t('mypage.profile.incompleteFields')}:</p>
            <div className="flex flex-wrap gap-2">
              {missingFields.map(f => (
                <span key={f.key} className="px-2 py-1 bg-red-50 text-red-700 text-sm rounded border border-red-200">{f.label}</span>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setProfileIncompleteDialog(false)}>{t('mypage.profile.close')}</Button>
            <Button className="bg-[#00A651] hover:bg-[#008a43] text-white" onClick={() => {
              setProfileIncompleteDialog(false);
              setEditMode(true);
              setActiveTab('profile');
              setTimeout(() => tabsContentRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
            }}>{t('mypage.profile.editProfile')}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Qualifications Dialog */}
      <Dialog open={qualificationsDialog} onOpenChange={setQualificationsDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Award className="h-5 w-5 text-[#00A651]" />
              {t('mp.myCerts')}
            </DialogTitle>
            <DialogDescription>{t('mp.certsList')}</DialogDescription>
          </DialogHeader>
          <div className="py-2">
            {loading ? (
              <div className="text-center py-8">
                <Loader2 className="h-8 w-8 animate-spin mx-auto text-[#00A651]" />
              </div>
            ) : myCertRequests.filter(r => r.status === 'completed').length === 0 && applications.filter(app => app.status === 'completed_passed' && app.certificateNumber).length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <GraduationCap className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                <p>{t('mp.noCerts')}</p>
                <p className="text-sm mt-1">{t('mp.completeToCert')}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Show ALL completed certificate requests assigned to this user */}
                {myCertRequests.filter(r => r.status === 'completed').map((cert) => (
                    <div key={cert.id}
                      className="border-2 border-[#00A651] bg-gradient-to-r from-[#e6f6ec] to-white rounded-lg p-4 cursor-pointer hover:shadow-md transition-shadow"
                      onClick={async () => {
                        setQualificationsDialog(false);
                        setViewCertData({ app: { courseName: cert.courseName, courseId: cert.courseId }, cert });
                        setCertImageUrl(null);
                        setCertImageLoading(true);
                        setViewCertDialog(true);
                        try {
                          const res = await axios.get(`${API}/users/me/certificate-requests/${cert.id}/image`, { headers: getAuthHeaders(), responseType: 'blob' });
                          setCertImageUrl(URL.createObjectURL(res.data));
                        } catch(e) { console.error(e); }
                        setCertImageLoading(false);
                      }}>
                      <div className="flex items-start gap-3">
                        <div className="bg-[#00A651] p-2 rounded-full"><GraduationCap className="h-5 w-5 text-white" /></div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-bold text-gray-900">{cert.courseName || cert.courseId}</h4>
                          <div className="mt-2 space-y-1">
                            {cert.certificateNumber && (
                              <p className="text-sm"><span className="text-gray-500">{t('mp.certNo')}</span> <span className="text-[#00A651] font-bold">{cert.certificateNumber}</span></p>
                            )}
                            {cert.completionDate && (
                              <p className="text-sm text-gray-600">{t('mp.completionDateLabel')} {cert.completionDate}</p>
                            )}
                          </div>
                          {(cert.type === 'digital' || (cert.types && cert.types.includes('digital'))) && (
                            <Button onClick={(e) => { e.stopPropagation(); handleDownloadCertificate(cert.id, cert.courseName || cert.courseId, cert.userName, 'cert-request'); }}
                              className="mt-2 bg-[#00A651] hover:bg-[#008a43] text-white" size="sm">
                              <Download className="h-3.5 w-3.5 mr-1.5" />{t('mypage.certificates.download')}
                            </Button>
                          )}
                        </div>
                        <Badge className="bg-[#00A651] text-white px-2 py-0.5 text-xs">{t('mypage.certificates.issued')}</Badge>
                      </div>
                    </div>
                ))}

                {/* Face-to-face courses with certificate number not in cert requests */}
                {applications.filter(app => app.status === 'completed_passed' && app.certificateNumber && !myCertRequests.some(r => r.courseId === app.courseId && r.status === 'completed')).map((app) => (
                  <div key={app.id}
                    className="border-2 border-[#00A651] bg-gradient-to-r from-[#e6f6ec] to-white rounded-lg p-4 cursor-pointer hover:shadow-md transition-shadow"
                    onClick={async () => {
                      setQualificationsDialog(false);
                      setViewCertData({ app, cert: { certificateNumber: app.certificateNumber, completionDate: app.completedAt, courseName: app.courseName } });
                      setCertImageUrl(null);
                      setCertImageLoading(true);
                      setViewCertDialog(true);
                      try {
                        const res = await axios.get(`${API}/users/me/certificates/${app.id}/image`, { headers: getAuthHeaders(), responseType: 'blob' });
                        setCertImageUrl(URL.createObjectURL(res.data));
                      } catch(e) { console.error(e); }
                      setCertImageLoading(false);
                    }}>
                    <div className="flex items-start gap-3">
                      <div className="bg-[#00A651] p-2 rounded-full"><GraduationCap className="h-5 w-5 text-white" /></div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-bold text-gray-900">{app.courseName || app.courseId}</h4>
                        <p className="text-sm mt-1"><span className="text-gray-500">{t('mp.certNo')}</span> <span className="text-[#00A651] font-bold">{app.certificateNumber}</span></p>
                        {app.completedAt && <p className="text-sm text-gray-600">{t('mp.acquired')} {formatDate(app.completedAt)}</p>}
                        <Button onClick={(e) => { e.stopPropagation(); handleDownloadCertificate(app.id, app.courseName || app.courseId, app.name); }}
                          className="mt-2 bg-[#00A651] hover:bg-[#008a43] text-white" size="sm">
                          <Download className="h-3.5 w-3.5 mr-1.5" />{t('mypage.certificates.download')}
                        </Button>
                      </div>
                      <Badge className="bg-[#00A651] text-white px-2 py-0.5 text-xs">{t('mp.valid')}</Badge>
                    </div>
                  </div>
                ))}

                {/* Pending certificates */}
                {applications.filter(app => app.status === 'completed_passed' && !app.certificateNumber && !myCertRequests.some(r => r.courseId === app.courseId && r.status === 'completed')).length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <p className="text-xs text-gray-500 mb-2">{t('mp.pendingCertNo')}</p>
                    {applications.filter(app => app.status === 'completed_passed' && !app.certificateNumber && !myCertRequests.some(r => r.courseId === app.courseId && r.status === 'completed')).map((app) => (
                      <div key={app.id} className="border border-gray-200 bg-gray-50 rounded-lg p-3 mb-2">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium text-gray-700 text-sm">{app.courseName || app.courseId}</h4>
                            {app.completedAt && <p className="text-xs text-gray-500">{t('mp.courseDate')} {formatDate(app.completedAt)}</p>}
                          </div>
                          <Badge className="bg-yellow-100 text-yellow-800 text-xs">{t('mp.pending')}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

            {/* Camera overlay for ID documents */}
      {mypageCameraTarget && (
        <DocumentCamera
          docType={idDocTypeMypage || 'residence_card'}
          side={mypageCameraTarget === 'idBack' ? 'back' : 'front'}
          onCapture={handleMypageCameraCapture}
          onClose={() => setMypageCameraTarget(null)}
        />
      )}

      {/* Profile photo prompt (users without a photo) */}
      <Dialog open={showFacePrompt} onOpenChange={(o) => { if (!o) dismissFacePrompt(); }}>
        <DialogContent className="max-w-sm" data-testid="face-prompt-dialog">
          <DialogHeader>
            <DialogTitle>{t('register.profilePromptTitle')}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center text-center gap-4 py-2">
            <div className="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center">
              <svg viewBox="0 0 100 100" className="w-16 h-16 text-gray-400" fill="currentColor">
                <circle cx="50" cy="38" r="20" />
                <path d="M18 92 Q18 62 50 62 Q82 62 82 92 Z" />
              </svg>
            </div>
            <p className="text-sm text-gray-600">{t('register.profilePromptDesc')}</p>
            <div className="flex gap-3 w-full">
              <Button variant="outline" className="flex-1" onClick={dismissFacePrompt} data-testid="face-prompt-later">
                {t('register.later')}
              </Button>
              <Button className="flex-1 bg-[#00A651] hover:bg-[#008a43]" onClick={() => { setShowFacePrompt(false); setShowFaceCamera(true); }} data-testid="face-prompt-take">
                {t('register.takeNow')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Face camera overlay */}
      {showFaceCamera && (
        <FaceCamera
          onCapture={handleFaceCaptured}
          onClose={() => setShowFaceCamera(false)}
        />
      )}
    </div>
  );
}
